import React, { useState, useEffect } from 'react';
import Layout from '../common/Layout';
import { apiService } from '../../services/api';
import { TrophyIcon } from '@heroicons/react/24/outline';

interface LeaderboardEntry {
  framework_id: number;
  framework_name: string;
  creator: string;
  description: string | null;
  quality_points: number;
  timing_points: number;
  total_trades: number;
  closed_trades: number;
  trades_pnl_unknown?: number;
  win_count: number | null;
  win_rate: number | null;          // fraction (gold), null when unknown
  total_stock_pnl: number | null;
  total_option_pnl: number | null;
  total_pnl: number | null;
  avg_pnl_per_trade: number | null;
}

const LeaderboardPage: React.FC = () => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/frameworks/leaderboard?min_trades=1`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.json())
      .then(data => { setEntries(data.leaderboard || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const fmtPnl = (v: number | null) => {
    if (v == null) return '—';
    const sign = v >= 0 ? '+' : '';
    return `${sign}$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  const tone = (v: number | null) => (v == null ? 'var(--color-text-muted)' : v >= 0 ? '#10b981' : '#ef4444');

  const rankColors = ['#fbbf24', '#94a3b8', '#cd7f32'];

  return (
    <Layout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <TrophyIcon style={{ width: 28, height: 28, color: '#fbbf24' }} />
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--color-white)' }}>Framework Leaderboard</h1>
        </div>

        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '20px' }}>
          Public scoring frameworks ranked by total P&L. Make your framework public in Settings to participate.
        </p>

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading...</div>
        ) : entries.length === 0 ? (
          <div className="frameworks-empty">
            <TrophyIcon style={{ width: 40, height: 40, margin: '0 auto 12px', color: 'var(--color-gray-700)' }} />
            <p>No public frameworks with tagged trades yet.</p>
            <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>
              Create a framework, tag your trades, and make it public to appear here.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {entries.map((entry, index) => (
              <div key={entry.framework_id} className="framework-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: index < 3 ? `${rankColors[index]}22` : 'var(--color-bg-tertiary)',
                    color: index < 3 ? rankColors[index] : 'var(--color-text-muted)',
                    fontWeight: 700, fontSize: '0.9rem', flexShrink: 0,
                  }}>
                    {index + 1}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-white)' }}>
                        {entry.framework_name}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>by {entry.creator}</span>
                    </div>
                    {entry.description && (
                      <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--color-text-muted)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {entry.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '12px', marginTop: '6px', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      <span>Q:{entry.quality_points} / T:{entry.timing_points}</span>
                      <span>{entry.closed_trades} trades</span>
                      {entry.win_rate != null && <span>{(entry.win_rate * 100).toFixed(1)}% win</span>}
                      {!!entry.trades_pnl_unknown && <span>{entry.trades_pnl_unknown} without a recorded close</span>}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '2px' }}>Stock P&L</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: tone(entry.total_stock_pnl) }}>
                      {fmtPnl(entry.total_stock_pnl)}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '2px' }}>Option P&L</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: tone(entry.total_option_pnl) }}>
                      {fmtPnl(entry.total_option_pnl)}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '80px' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '2px' }}>Total P&L</div>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: tone(entry.total_pnl) }}>
                      {fmtPnl(entry.total_pnl)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LeaderboardPage;
